import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Download, RefreshCw, Search, Wrench } from 'lucide-react';
import api from '../../services/api';
import { useToast } from '../../contexts/ToastContext';

/**
 * Money taken, per event and in total, with the ability to refund.
 *
 * The endpoints behind this existed long before the screen did — refunds,
 * totals and per-event payments were reachable only by calling the API directly.
 */

interface PaymentRow {
  _id: string;
  amount: number;
  currency?: string;
  status: string;
  razorpayPaymentId?: string;
  createdAt?: string;
  refundReason?: string;
  user?: { firstName?: string; lastName?: string; email?: string };
  event?: { _id: string; title?: string };
  hackathon?: { title?: string };
}

interface Summary {
  collected: number;
  collectedCount: number;
  refunded: number;
  refundedCount: number;
  net: number;
}

interface EventOption { _id: string; title: string }

const rupees = (paise: number) =>
  `₹${(paise / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const when = (iso?: string) =>
  iso ? new Date(iso).toLocaleString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit'
  }) : '—';

const payerName = (p: PaymentRow) =>
  `${p.user?.firstName || ''} ${p.user?.lastName || ''}`.trim() || 'Unknown';

export default function PaymentsPanel({ events }: { events: EventOption[] }) {
  const { success, error } = useToast();
  const [rows, setRows] = useState<PaymentRow[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(true);
  const [eventId, setEventId] = useState('');
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [refunding, setRefunding] = useState<string | null>(null);

  // Payments Razorpay has taken that this database cannot account for.
  const [orphans, setOrphans] = useState<any[] | null>(null);
  const [checking, setChecking] = useState(false);
  const [repairing, setRepairing] = useState<string | null>(null);

  const reconcile = async () => {
    setChecking(true);
    try {
      const res = await api.get('/payments/admin/reconcile');
      setOrphans(res.data.orphans || []);
      const n = (res.data.orphans || []).length;
      if (n === 0) {
        success(`Checked ${res.data.examined} payments at Razorpay — all accounted for.`);
      }
    } catch (err: any) {
      error(err.response?.data?.message || 'Could not check against Razorpay');
    } finally {
      setChecking(false);
    }
  };

  const repair = async (o: any) => {
    const who = o.user?.name || o.contactEmail || o.paymentId;
    if (!window.confirm(
      `Create the missing registration for ${who}?\n\n` +
      `${o.event?.title || 'Unknown event'} — ${rupees(o.amount)}\n\n` +
      'They will be registered and emailed a confirmation and receipt. ' +
      'No money moves; this only records what was already paid.'
    )) return;

    setRepairing(o.paymentId);
    try {
      const res = await api.post(`/payments/admin/reconcile/${o.paymentId}`);
      success(res.data.message);
      reconcile();
      load();
    } catch (err: any) {
      // The fee has moved since this was paid. The payment is still real, so
      // the server asks rather than refuses, and records the sum actually paid.
      if (err.response?.status === 409 && err.response.data?.needsConfirmation) {
        if (window.confirm(`${err.response.data.message}\n\nRecord it as paid?`)) {
          try {
            const res = await api.post(`/payments/admin/reconcile/${o.paymentId}`, { confirmAmountMismatch: true });
            success(res.data.message);
            reconcile();
            load();
          } catch (retryErr: any) {
            error(retryErr.response?.data?.message || 'Could not rebuild that registration');
          }
        }
      } else {
        error(err.response?.data?.message || 'Could not rebuild that registration');
      }
    } finally {
      setRepairing(null);
    }
  };


  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (eventId) params.set('eventId', eventId);
      if (status !== 'all') params.set('status', status);
      if (search.trim()) params.set('search', search.trim());
      const res = await api.get(`/payments/admin/list?${params.toString()}`);
      setRows(res.data.payments || []);
      setSummary(res.data.summary || null);
    } catch (err: any) {
      error(err.response?.data?.message || 'Could not load payments');
    } finally {
      setLoading(false);
    }
  }, [eventId, status, search, error]);

  useEffect(() => { load(); }, [eventId, status]); // eslint-disable-line react-hooks/exhaustive-deps

  const refund = async (row: PaymentRow) => {
    const who = payerName(row);
    const reason = window.prompt(
      `Refund ${rupees(row.amount)} to ${who}?\n\n` +
      'This sends the money back through Razorpay and cannot be undone here.\n\n' +
      'Reason (recorded against the payment):'
    );
    if (reason === null) return;

    setRefunding(row._id);
    try {
      await api.post(`/payments/${row._id}/refund`, { reason: reason || 'Admin refund' });
      success(`Refunded ${rupees(row.amount)} to ${who}`);
      load();
    } catch (err: any) {
      error(err.response?.data?.message || 'Refund failed');
    } finally {
      setRefunding(null);
    }
  };

  const exportCsv = () => {
    const head = ['Payer', 'Email', 'Event', 'Amount (INR)', 'Status', 'Payment ID', 'Date'];
    const lines = rows.map((p) => [
      payerName(p),
      p.user?.email || '',
      p.event?.title || p.hackathon?.title || '',
      (p.amount / 100).toFixed(2),
      p.status,
      p.razorpayPaymentId || '',
      when(p.createdAt)
    ].map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','));

    const blob = new Blob([[head.join(','), ...lines].join('\n')], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `payments-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="space-y-6">
      {summary && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <p className="text-sm text-gray-500">Collected</p>
            <p className="mt-1 text-2xl font-semibold text-gray-900">{rupees(summary.collected)}</p>
            <p className="text-xs text-gray-500">{summary.collectedCount} payment{summary.collectedCount === 1 ? '' : 's'}</p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <p className="text-sm text-gray-500">Refunded</p>
            <p className="mt-1 text-2xl font-semibold text-gray-900">{rupees(summary.refunded)}</p>
            <p className="text-xs text-gray-500">{summary.refundedCount} refund{summary.refundedCount === 1 ? '' : 's'}</p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <p className="text-sm text-gray-500">Net</p>
            <p className="mt-1 text-2xl font-semibold text-green-700">{rupees(summary.net)}</p>
            <p className="text-xs text-gray-500">Collected less refunds</p>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label htmlFor="pay-event" className="mb-1 block text-sm font-medium text-gray-700">Event</label>
          <select
            id="pay-event"
            value={eventId}
            onChange={(e) => setEventId(e.target.value)}
            className="rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All events</option>
            {events.map((e) => <option key={e._id} value={e._id}>{e.title}</option>)}
          </select>
        </div>

        <div>
          <label htmlFor="pay-status" className="mb-1 block text-sm font-medium text-gray-700">Status</label>
          <select
            id="pay-status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All</option>
            <option value="completed">Completed</option>
            <option value="refunded">Refunded</option>
            <option value="failed">Failed</option>
          </select>
        </div>

        <div className="flex-1 min-w-[220px]">
          <label htmlFor="pay-search" className="mb-1 block text-sm font-medium text-gray-700">Search</label>
          <div className="flex gap-2">
            <input
              id="pay-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') load(); }}
              placeholder="Name, email or payment id"
              className="flex-1 rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              type="button"
              onClick={load}
              className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
            >
              <Search className="h-4 w-4" /> Search
            </button>
          </div>
        </div>

        <button
          type="button"
          onClick={load}
          className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
        >
          <RefreshCw className="h-4 w-4" /> Refresh
        </button>

        <button
          type="button"
          onClick={reconcile}
          disabled={checking}
          className="inline-flex items-center gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-4 py-2 text-sm font-medium text-amber-900 hover:bg-amber-100 disabled:opacity-50"
        >
          <Wrench className="h-4 w-4" /> {checking ? 'Checking Razorpay…' : 'Check against Razorpay'}
        </button>

        <button
          type="button"
          onClick={exportCsv}
          disabled={rows.length === 0}
          className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          <Download className="h-4 w-4" /> Export CSV
        </button>
      </div>

      {orphans !== null && (
        <div className={`rounded-lg border p-5 ${orphans.length ? 'border-amber-300 bg-amber-50' : 'border-green-200 bg-green-50'}`}>
          {orphans.length === 0 ? (
            <p className="text-sm text-green-900">
              Every payment Razorpay has is accounted for here.
            </p>
          ) : (
            <>
              <h3 className="mb-1 flex items-center gap-2 font-semibold text-amber-900">
                <AlertTriangle className="h-5 w-5" />
                {orphans.length} payment{orphans.length === 1 ? '' : 's'} with no registration
              </h3>
              <p className="mb-4 text-sm text-amber-900">
                Razorpay took this money and nothing here recorded it. Repairing creates the
                registration the payment should have produced, and emails the payer.
              </p>
              <ul className="space-y-3">
                {orphans.map((o) => (
                  <li key={o.paymentId} className="rounded-md border border-amber-200 bg-white p-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900">
                          {o.user?.name || o.contactEmail || 'Unknown payer'}
                          <span className="ml-2 font-normal text-gray-600">{rupees(o.amount)}</span>
                        </p>
                        <p className="text-sm text-gray-600">
                          {o.event?.title || 'Event not identified'}
                          {o.capturedAt && <> · {when(o.capturedAt)}</>}
                        </p>
                        <p className="mt-1 break-all text-xs text-gray-500">
                          {o.paymentId}{o.user?.email ? ` · ${o.user.email}` : ''}
                        </p>
                        {o.blocked && (
                          <p className="mt-2 rounded bg-amber-100 px-2 py-1 text-xs text-amber-900">{o.blocked}</p>
                        )}
                        {o.hasRegistrationAlready && !o.blocked && (
                          <p className="mt-2 text-xs text-gray-600">
                            A registration already exists for this person and event — repairing will
                            attach the payment to it rather than create a second.
                          </p>
                        )}
                      </div>
                      {!o.blocked && (
                        <button
                          type="button"
                          onClick={() => repair(o)}
                          disabled={repairing === o.paymentId}
                          className="shrink-0 rounded-md bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-50"
                        >
                          {repairing === o.paymentId ? 'Creating…' : 'Create the registration'}
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wide text-gray-500">
              <th className="px-4 py-3 font-semibold">Payer</th>
              <th className="px-4 py-3 font-semibold">Event</th>
              <th className="px-4 py-3 font-semibold">Amount</th>
              <th className="px-4 py-3 font-semibold">Status</th>
              <th className="px-4 py-3 font-semibold">Date</th>
              <th className="px-4 py-3 font-semibold">Payment ID</th>
              <th className="px-4 py-3 font-semibold"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                No payments{eventId || status !== 'all' || search ? ' match this filter' : ' yet'}.
              </td></tr>
            )}
            {!loading && rows.map((p) => (
              <tr key={p._id} className="border-b border-gray-100 last:border-0">
                <td className="px-4 py-3">
                  <div className="font-medium text-gray-900">{payerName(p)}</div>
                  <div className="text-xs text-gray-500">{p.user?.email}</div>
                </td>
                <td className="px-4 py-3 text-gray-700">{p.event?.title || p.hackathon?.title || '—'}</td>
                <td className="px-4 py-3 font-medium text-gray-900">{rupees(p.amount)}</td>
                <td className="px-4 py-3">
                  <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
                    p.status === 'completed' ? 'bg-green-100 text-green-800'
                    : p.status === 'refunded' ? 'bg-amber-100 text-amber-800'
                    : 'bg-gray-100 text-gray-700'
                  }`}>
                    {p.status}
                  </span>
                  {p.refundReason && <div className="mt-1 text-xs text-gray-500">{p.refundReason}</div>}
                </td>
                <td className="px-4 py-3 text-gray-600">{when(p.createdAt)}</td>
                <td className="px-4 py-3">
                  <code className="text-xs text-gray-600">{p.razorpayPaymentId || '—'}</code>
                </td>
                <td className="px-4 py-3 text-right">
                  {p.status === 'completed' && (
                    <button
                      type="button"
                      onClick={() => refund(p)}
                      disabled={refunding === p._id}
                      className="rounded-md border border-red-300 px-3 py-1.5 text-sm text-red-700 hover:bg-red-50 disabled:opacity-50"
                    >
                      {refunding === p._id ? 'Refunding…' : 'Refund'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
